import { useForm, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAdminLessons, AdminLesson } from '@/hooks/useAdminCourse';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useState, useEffect } from 'react';
import { PdfUpload } from './PdfUpload';
import { LessonTranscriptUpload } from './LessonTranscriptUpload';
import { RichTextEditor } from './RichTextEditor';
import { InstructorSelector } from './InstructorSelector';
import { useBrandManager } from '@/contexts/BrandProvider';
import { LmsTagInput } from './LmsTagInput';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Mail, Loader2, X, Plus } from 'lucide-react';
import { queueLessonAddedEmails } from '@/lib/lms/queueLmsEmail';
import { useToast } from '@/hooks/use-toast';
import { useLessonEditor } from '@/contexts/LessonEditorContext';
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle, DrawerDescription } from '@/components/ui/drawer';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useSidebar } from '@/components/ui/sidebar';
import { LessonDigcompInfo } from './LessonDigcompInfo';
import { useLmsGeneralSettings } from '@/hooks/useLmsGeneralSettings';

const instructionCueSchema = z
  .object({
    start_seconds: z.coerce.number().min(0, 'Start must be ≥ 0'),
    end_seconds: z.preprocess(
      (val) => (val === '' || val == null ? undefined : val),
      z.number().optional()
    ),
    text: z.string().min(1, 'Text is required'),
  })
  .refine((data) => data.end_seconds == null || data.end_seconds > data.start_seconds, {
    message: 'End time must be after start time',
    path: ['end_seconds'],
  });

const lessonSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters'),
  description: z.string().optional(),
  content_type: z.enum(['video', 'text', 'pdf', 'quiz', 'mixed']),
  duration: z.coerce.number().min(1, 'Duration must be at least 1 minute'),
  is_preview: z.boolean(),
  is_published: z.boolean(),
  instructor_ids: z.array(z.string().uuid('Invalid instructor ID')).default([]),
  video_url: z.string().optional(),
  text_content: z.string().optional(),
  pdf_url: z.string().optional(),
  material_url: z.string().optional(),
  quiz_ids: z.array(z.string()).optional(),
  transcript: z.string().optional(),
  instruction_cues: z.array(instructionCueSchema).optional().default([]),
});

type LessonFormData = z.infer<typeof lessonSchema>;

export function GlobalLessonEditor() {
  const { isOpen, moduleId, lesson, nextSortOrder, closeEditor } = useLessonEditor();
  const { createLesson, updateLesson } = useAdminLessons(moduleId || '');
  const { currentBrand } = useBrandManager();
  const { toast } = useToast();
  const { state: sidebarState, isMobile } = useSidebar();
  const isNew = !lesson;
  const [isSendingNotification, setIsSendingNotification] = useState(false);

  const existingContent = lesson?.content || {};
  const [contentType, setContentType] = useState<string>(lesson?.content_type || 'text');

  // Calculate drawer width and top offset based on sidebar state
  const SIDEBAR_WIDTH = '16rem';
  const SIDEBAR_WIDTH_ICON = '3rem';
  const MOBILE_HEADER_HEIGHT = '57px';
  
  const drawerWidth = isMobile 
    ? '100%' 
    : sidebarState === 'expanded' 
      ? `calc(100% - ${SIDEBAR_WIDTH})` 
      : `calc(100% - ${SIDEBAR_WIDTH_ICON})`;
  
  const drawerTop = isMobile ? MOBILE_HEADER_HEIGHT : '0';

  const { data: lmsSettings } = useLmsGeneralSettings(currentBrand?.id || null);
  const digcompEnabled = lmsSettings?.digcomp_integration_enabled ?? false;

  // Fetch active lesson_added email reminders for this course
  const { data: activeLessonEmails } = useQuery({
    queryKey: ['active-lesson-emails', moduleId],
    queryFn: async () => {
      if (!moduleId) return [];
      const { data: moduleData } = await supabase
        .from('os_lms_modules')
        .select('course_id')
        .eq('id', moduleId)
        .single();
      if (!moduleData?.course_id) return [];
      const { data, error } = await supabase
        .from('os_lms_emails')
        .select('id, subject, trigger_type, is_active')
        .eq('course_id', moduleData.course_id)
        .eq('trigger_type', 'lesson_added')
        .eq('is_active', true);
      if (error) throw error;
      return data || [];
    },
    enabled: !!moduleId,
  });

  // Fetch available quizzes for the brand (only informational quizzes for lesson quiz content)
  const { data: availableQuizzes } = useQuery({
    queryKey: ['available-quizzes', currentBrand?.id],
    queryFn: async () => {
      if (!currentBrand?.id) return [];
      const { data, error } = await supabase
        .from('os_lms_quizzes')
        .select('id, title, description, quiz_purpose')
        .eq('brand_id', currentBrand.id)
        .eq('quiz_purpose', 'informational')
        .order('title');
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentBrand?.id,
  });

  const form = useForm<LessonFormData>({
    resolver: zodResolver(lessonSchema),
    mode: 'onBlur',
    defaultValues: {
      title: lesson?.title || '',
      description: lesson?.description || '',
      content_type: lesson?.content_type || 'text',
      duration: lesson?.duration || 10,
      is_preview: lesson?.is_preview || false,
      is_published: lesson?.is_published || false,
      instructor_ids: Array.isArray((lesson as any)?.instructor_id) 
        ? (lesson as any).instructor_id 
        : (lesson as any)?.instructor_ids || ((lesson as any)?.instructor_id ? [(lesson as any).instructor_id] : []),
      video_url: existingContent.video_url || '',
      text_content: existingContent.text_content || '',
      pdf_url: existingContent.pdf_url || '',
      material_url: lesson?.material_url || '',
      quiz_ids: existingContent.quiz_ids || [],
      transcript: lesson?.transcript || '',
      instruction_cues: existingContent.instruction_cues ?? [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'instruction_cues',
  });

  // Reset form when lesson changes
  useEffect(() => {
    if (lesson) {
      const content = lesson.content || {};
      form.reset({
        title: lesson.title || '',
        description: lesson.description || '',
        content_type: lesson.content_type || 'text',
        duration: lesson.duration || 10,
        is_preview: lesson.is_preview || false,
        is_published: lesson.is_published || false,
        instructor_ids: (lesson as any)?.instructor_ids || (Array.isArray((lesson as any)?.instructor_id) 
          ? (lesson as any).instructor_id 
          : ((lesson as any)?.instructor_id ? [(lesson as any).instructor_id] : [])),
        video_url: content.video_url || '',
        text_content: content.text_content || '',
        pdf_url: content.pdf_url || '',
        material_url: lesson.material_url || '',
        quiz_ids: content.quiz_ids || [],
        transcript: lesson.transcript || '',
        instruction_cues: content.instruction_cues ?? [],
      });
      setContentType(lesson.content_type || 'text');
    } else {
      form.reset({
        title: '',
        description: '',
        content_type: 'text',
        duration: 10,
        is_preview: false,
        is_published: false,
        instructor_ids: [],
        video_url: '',
        text_content: '',
        pdf_url: '',
        material_url: '',
        quiz_ids: [],
        transcript: '',
        instruction_cues: [],
      });
      setContentType('text');
    }
  }, [lesson, form]);

  const onSubmit = (data: LessonFormData) => {
    if (!currentBrand?.id || !moduleId) return;
    
    const { video_url, text_content, pdf_url, content_type: ct, material_url, description, instructor_ids, quiz_ids, transcript, instruction_cues, ...rest } = data;

    const sortedCues = (instruction_cues ?? [])
      .slice()
      .sort((a, b) => a.start_seconds - b.start_seconds);

    // Build content object based on type
    let content: any = {};
    if (ct === 'video') {
      content = { video_url, instruction_cues: sortedCues };
    } else if (ct === 'text') {
      content = { text_content };
    } else if (ct === 'pdf') {
      content = { pdf_url };
    } else if (ct === 'mixed') {
      content = { video_url, text_content, instruction_cues: sortedCues };
    } else if (ct === 'quiz') {
      content = { quiz_ids: quiz_ids || [] };
    }

    const lessonData: any = {
      brand_id: currentBrand.id,
      module_id: moduleId,
      title: data.title,
      description: description || null,
      content_type: ct,
      content,
      duration: data.duration,
      is_preview: data.is_preview,
      is_published: data.is_published,
      sort_order: lesson?.sort_order ?? nextSortOrder,
      material_url: material_url || null,
      instructor_id: instructor_ids && instructor_ids.length > 0 ? instructor_ids : null,
      transcript: transcript || null,
    };

    if (isNew) {
      createLesson(lessonData, {
        onSuccess: () => {
          form.reset();
          closeEditor();
        },
      });
    } else {
      const updatePayload: any = {
        id: lesson.id, 
        title: data.title,
        description: description || null,
        content_type: ct, 
        content, 
        duration: data.duration,
        is_preview: data.is_preview,
        is_published: data.is_published,
        material_url: material_url || null,
        instructor_id: instructor_ids && instructor_ids.length > 0 ? instructor_ids : null,
        transcript: transcript || null,
        wasPublished: lesson.is_published, // Track previous state for email trigger
      };
      
      updateLesson(updatePayload, {
        onSuccess: () => {
          closeEditor();
        },
      });
    }
  };

  if (!moduleId) return null;

  return (
    <Drawer open={isOpen} onOpenChange={(open) => !open && closeEditor()} direction="right" modal={false}>
      <DrawerContent 
        className="max-w-none right-0 left-auto rounded-none mt-0 fixed bg-background border-l"
        style={{ 
          width: drawerWidth, 
          top: drawerTop,
          height: isMobile ? `calc(100% - ${MOBILE_HEADER_HEIGHT})` : '100%'
        }}
      >
        <div className="flex flex-col h-full">
          <DrawerHeader className="flex-shrink-0 border-b px-4 sm:px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <DrawerTitle>{isNew ? 'Create Lesson' : 'Edit Lesson'}</DrawerTitle>
                <DrawerDescription>
                  {isNew ? 'Add a new lesson to this module' : 'Update lesson information'}
                </DrawerDescription>
              </div>
              <Button variant="ghost" size="icon" onClick={closeEditor}>
                <X className="h-5 w-5" />
              </Button>
            </div>
          </DrawerHeader>

          <div className="flex-1 flex flex-col min-h-0">
            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
                <Tabs defaultValue="content" className="flex-1 flex flex-col min-h-0 px-4 sm:px-6">
                  {(digcompEnabled && moduleId) ? (
                    <TabsList className="grid w-full grid-cols-2 shrink-0 mt-2">
                      <TabsTrigger value="content">Content</TabsTrigger>
                      <TabsTrigger value="digcomp">DigComp</TabsTrigger>
                    </TabsList>
                  ) : null}

                  <ScrollArea className="flex-1 min-h-0 mt-4 h-[min(500px,55vh)]">
                    <TabsContent value="content" className="space-y-4 mt-0 pb-4">
                      <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                      <FormItem>
                        <FormLabel>Lesson Title</FormLabel>
                        <FormControl>
                          <Input placeholder="Introduction to the topic" {...field} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="description"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Description (Optional)</FormLabel>
                        <FormControl>
                          <RichTextEditor 
                            content={field.value || ''}
                            onChange={field.onChange}
                            placeholder="Brief description of what this lesson covers"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                        )}
                      />

                      <LmsTagInput
                        entityType="lesson"
                        entityId={lesson?.id || null}
                        brandId={currentBrand?.id || ''}
                        currentTagIds={(lesson as any)?.tag_ids || []}
                      />

                      <div className="grid grid-cols-2 gap-4">
                        <FormField
                      control={form.control}
                      name="content_type"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Content Type</FormLabel>
                          <Select
                            onValueChange={(value) => {
                              field.onChange(value);
                              setContentType(value);
                            }}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select type" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem value="text">Text</SelectItem>
                              <SelectItem value="video">Video</SelectItem>
                              <SelectItem value="pdf">PDF</SelectItem>
                              <SelectItem value="quiz">Quiz</SelectItem>
                              <SelectItem value="mixed">Mixed</SelectItem>
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="duration"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Duration (minutes)</FormLabel>
                          <FormControl>
                            <Input type="number" min={1} {...field} />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  </div>

                  {/* Content fields based on type */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <h4 className="font-medium">Content</h4>
                    
                    {(contentType === 'video' || contentType === 'mixed') && (
                      <>
                        <FormField
                          control={form.control}
                          name="video_url"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Video URL</FormLabel>
                              <FormControl>
                                <Input placeholder="https://youtube.com/watch?v=... or https://vimeo.com/..." {...field} />
                              </FormControl>
                              <FormDescription>YouTube, Vimeo, or direct video link</FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label>Video instruction cues</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => append({ start_seconds: 0, text: '' })}
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Add cue
                            </Button>
                          </div>
                          <FormDescription>Show instructions at specific timestamps while the video plays (e.g. &quot;Pause and try this&quot;).</FormDescription>
                          <div className="space-y-3 max-h-64 overflow-y-auto border rounded-md p-3">
                            {fields.map((field, index) => (
                              <div key={field.id} className="flex flex-col gap-2 p-2 border rounded bg-muted/30">
                                <div className="flex gap-2 items-center flex-wrap">
                                  <FormField
                                    control={form.control}
                                    name={`instruction_cues.${index}.start_seconds`}
                                    render={({ field: f }) => (
                                      <FormItem className="flex-1 min-w-[100px]">
                                        <FormLabel className="text-xs">Start (sec)</FormLabel>
                                        <FormControl>
                                          <Input type="number" min={0} step={1} {...f} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <FormField
                                    control={form.control}
                                    name={`instruction_cues.${index}.end_seconds`}
                                    render={({ field: f }) => (
                                      <FormItem className="flex-1 min-w-[100px]">
                                        <FormLabel className="text-xs">End (sec, optional)</FormLabel>
                                        <FormControl>
                                          <Input type="number" min={0} step={1} placeholder="—" {...f} />
                                        </FormControl>
                                        <FormMessage />
                                      </FormItem>
                                    )}
                                  />
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    className="shrink-0 mt-6"
                                    onClick={() => remove(index)}
                                  >
                                    <X className="h-4 w-4" />
                                  </Button>
                                </div>
                                <FormField
                                  control={form.control}
                                  name={`instruction_cues.${index}.text`}
                                  render={({ field: f }) => (
                                    <FormItem>
                                      <FormControl>
                                        <Textarea placeholder="Instruction text (markdown supported)" rows={2} className="resize-none" {...f} />
                                      </FormControl>
                                      <FormMessage />
                                    </FormItem>
                                  )}
                                />
                              </div>
                            ))}
                            {fields.length === 0 && (
                              <p className="text-sm text-muted-foreground">No cues. Add cues to show instructions at specific times during playback.</p>
                            )}
                          </div>
                        </div>
                      </>
                    )}

                    {(contentType === 'text' || contentType === 'mixed') && (
                      <FormField
                        control={form.control}
                        name="text_content"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Text Content</FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Write your lesson content here..."
                                rows={6}
                                {...field}
                              />
                            </FormControl>
                            <FormDescription>Supports markdown formatting</FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {contentType === 'pdf' && (
                      <FormField
                        control={form.control}
                        name="pdf_url"
                        render={({ field }) => (
                          <FormItem>
                            <PdfUpload
                              label="PDF URL"
                              description="Upload PDF or enter URL manually"
                              currentUrl={field.value}
                              onUploadComplete={(url) => field.onChange(url)}
                            />
                            <FormControl>
                              <Input placeholder="Or paste URL: https://example.com/document.pdf" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}

                    {contentType === 'quiz' && (
                      <FormField
                        control={form.control}
                        name="quiz_ids"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>Select Quizzes (of Type Informational)</FormLabel>
                            <div className="border rounded-md p-3 space-y-2 max-h-48 overflow-y-auto">
                              {availableQuizzes && availableQuizzes.length > 0 ? (
                                availableQuizzes.map((quiz) => (
                                  <div key={quiz.id} className="flex items-center space-x-2">
                                    <Checkbox
                                      id={quiz.id}
                                      checked={field.value?.includes(quiz.id) || false}
                                      onCheckedChange={(checked) => {
                                        const current = field.value || [];
                                        if (checked) {
                                          field.onChange([...current, quiz.id]);
                                        } else {
                                          field.onChange(current.filter((id: string) => id !== quiz.id));
                                        }
                                      }}
                                    />
                                    <Label htmlFor={quiz.id} className="text-sm cursor-pointer">
                                      {quiz.title}
                                    </Label>
                                  </div>
                                ))
                              ) : (
                                <p className="text-sm text-muted-foreground">
                                  No quizzes available. Create quizzes in Quiz Management first.
                                </p>
                              )}
                            </div>
                            <FormDescription>
                              Select one or more quizzes from Quiz Management
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    )}
                  </div>

                  {/* Instructor Selector */}
                  <FormField
                    control={form.control}
                    name="instructor_ids"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Instructors</FormLabel>
                        <FormControl>
                          <InstructorSelector
                            brandId={currentBrand?.id || ''}
                            value={Array.isArray(field.value) ? field.value : []}
                            onChange={(value) => {
                              field.onChange(value);
                            }}
                            multiple={true}
                          />
                        </FormControl>
                        <FormDescription>
                          Select one or more instructors for this lesson
                        </FormDescription>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  {/* Additional Materials Section */}
                  {contentType !== 'pdf' && (
                    <div className="border rounded-lg p-4 space-y-4">
                      <h4 className="font-medium">Additional Materials (Optional)</h4>
                      <FormField
                        control={form.control}
                        name="material_url"
                        render={({ field }) => (
                          <FormItem>
                            <PdfUpload
                              label="Supplemental PDF"
                              description="Upload additional materials for students"
                              currentUrl={field.value}
                              onUploadComplete={(url) => field.onChange(url)}
                            />
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}

                  {/* Transcript Section */}
                  <FormField
                    control={form.control}
                    name="transcript"
                    render={({ field }) => (
                      <FormItem>
                        <LessonTranscriptUpload
                          value={field.value || ''}
                          onChange={field.onChange}
                        />
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <div className="space-y-3">
                    <FormField
                      control={form.control}
                      name="is_preview"
                      render={({ field }) => (
                        <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                          <div className="space-y-0.5">
                            <FormLabel>Preview Lesson</FormLabel>
                            <FormDescription>Allow non-enrolled students to view</FormDescription>
                          </div>
                          <FormControl>
                            <Switch checked={field.value} onCheckedChange={field.onChange} />
                          </FormControl>
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="is_published"
                      render={({ field }) => (
                        <FormItem className="flex flex-col rounded-lg border p-3 gap-2">
                          <div className="flex flex-row items-center justify-between">
                            <div className="space-y-0.5">
                              <FormLabel>Published</FormLabel>
                              <FormDescription>Make visible to enrolled students</FormDescription>
                            </div>
                            <FormControl>
                              <Switch checked={field.value} onCheckedChange={field.onChange} />
                            </FormControl>
                          </div>
                          {!field.value && !lesson?.is_published && activeLessonEmails && activeLessonEmails.length > 0 && (
                            <div className="text-xs text-amber-600 dark:text-amber-400 flex items-start gap-1.5">
                              <Mail className="h-3.5 w-3.5 shrink-0 mt-0.5" />
                              <div>
                                <p className="font-medium">Active email reminder{activeLessonEmails.length > 1 ? 's' : ''} for this course:</p>
                                <ul className="list-disc list-inside mt-0.5">
                                  {activeLessonEmails.map(email => (
                                    <li key={email.id}>"{email.subject}"</li>
                                  ))}
                                </ul>
                                <p className="mt-1">Turning this on will trigger email delivery to all enrolled users.</p>
                              </div>
                            </div>
                          )}
                          {!field.value && !lesson?.is_published && (!activeLessonEmails || activeLessonEmails.length === 0) && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                              <Mail className="h-3.5 w-3.5 shrink-0" />
                              No active "Lesson Added" email reminders configured for this course.
                            </p>
                          )}
                          {field.value && lesson?.is_published && (
                            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
                              <Mail className="h-3.5 w-3.5 shrink-0" />
                              This lesson is already published. Use "Send Notification" to manually notify enrolled users of updates.
                            </p>
                          )}
                        </FormItem>
                      )}
                    />
                  </div>
                    </TabsContent>

                    {digcompEnabled && moduleId && (
                      <TabsContent value="digcomp" className="mt-0 pb-4">
                        {lesson ? (
                          <LessonDigcompInfo
                            lesson={{
                              id: lesson.id,
                              title: form.watch('title') || lesson.title,
                              description: form.watch('description') || lesson.description,
                              transcript: form.watch('transcript') || lesson.transcript,
                              content: { text_content: form.watch('text_content') || (lesson.content as any)?.text_content },
                            }}
                            moduleId={moduleId}
                            brandId={currentBrand?.id || ''}
                          />
                        ) : (
                          <p className="text-sm text-muted-foreground py-4">
                            Save the lesson first to add DigComp competences.
                          </p>
                        )}
                      </TabsContent>
                    )}
                  </ScrollArea>
                </Tabs>

                <div className="flex justify-between items-center gap-2 pt-4 border-t shrink-0 px-4 sm:px-6 pb-4">
                    {/* Manual email notification trigger for existing published lessons */}
                    {!isNew && lesson?.is_published && (
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isSendingNotification}
                        onClick={async () => {
                          if (!currentBrand?.id || !lesson || !moduleId) return;
                          
                          setIsSendingNotification(true);
                          try {
                            // Get course info from module
                            const { data: moduleData } = await supabase
                              .from('os_lms_modules')
                              .select('course_id, os_lms_courses(title, brand_id)')
                              .eq('id', moduleId)
                              .single();

                            if (moduleData?.course_id && (moduleData as any).os_lms_courses) {
                              const course = (moduleData as any).os_lms_courses;
                              const result = await queueLessonAddedEmails({
                                brandId: course.brand_id,
                                courseId: moduleData.course_id,
                                courseTitle: course.title,
                                lessonName: lesson.title,
                              });
                              
                              if (result.queued > 0) {
                                toast({
                                  title: `${result.queued} email(s) queued`,
                                  description: `Notifications sent for "${lesson.title}"`,
                                });
                              } else if (result.errors.length > 0) {
                                toast({
                                  title: 'Email queuing failed',
                                  description: result.errors[0],
                                  variant: 'destructive',
                                });
                              } else {
                                toast({
                                  title: 'No emails queued',
                                  description: 'No active enrollments or email template found',
                                });
                              }
                            } else {
                              toast({
                                title: 'Error',
                                description: 'Could not find course information',
                                variant: 'destructive',
                              });
                            }
                          } catch (error: any) {
                            toast({
                              title: 'Error sending notification',
                              description: error?.message || 'Failed to queue emails',
                              variant: 'destructive',
                            });
                          } finally {
                            setIsSendingNotification(false);
                          }
                        }}
                      >
                        {isSendingNotification ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <Mail className="h-4 w-4 mr-2" />
                        )}
                        Send Notification
                      </Button>
                    )}
                    
                    <div className="flex gap-2 ml-auto">
                      <Button type="button" variant="outline" onClick={closeEditor}>
                        Cancel
                      </Button>
                      <Button type="submit">{isNew ? 'Create' : 'Save'}</Button>
                    </div>
                </div>
              </form>
            </Form>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
